import React, { useMemo, useState } from "react";
import { FiCalendar, FiDownload, FiFilter, FiDollarSign, FiTag, FiTrendingDown, FiTrendingUp } from "react-icons/fi";

import SectionHeader from "../components/ui/SectionHeader";
import StatCard from "../components/ui/StatCard";
import { useGetCashflowsQuery, useGetPaymentsQuery } from "../services/api";
import { money, shortDate } from "../utils/formatters";

const CATEGORIES = [
  { value: "all", label: "All Categories" },
  { value: "salary", label: "Salary / Payroll" },
  { value: "rent", label: "Rent" },
  { value: "utilities", label: "Utilities (WiFi, Electric, Water)" },
  { value: "supplies", label: "Office Supplies" },
  { value: "maintenance", label: "Maintenance & Repairs" },
  { value: "marketing", label: "Marketing" },
  { value: "transport", label: "Transport" },
  { value: "other", label: "Other" },
];

const CATEGORY_COLORS = {
  salary: "#6366f1",
  rent: "#f59e0b",
  utilities: "#3b82f6",
  supplies: "#10b981",
  maintenance: "#ef4444",
  marketing: "#8b5cf6",
  transport: "#14b8a6",
  other: "#6b7280",
};

const CategoryDot = ({ category }) => (
  <span
    style={{
      display: "inline-block",
      width: 10,
      height: 10,
      borderRadius: "50%",
      background: CATEGORY_COLORS[category] || "#6b7280",
      marginRight: 6,
      flexShrink: 0,
    }}
  />
);

function formatCurrency(value) {
  return Number(value || 0).toLocaleString("en-ET", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const ExpenseHistoryPage = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(true);

  const { data, isLoading } = useGetCashflowsQuery();
  const { data: paymentsData, isLoading: paymentsLoading } = useGetPaymentsQuery();
  
  const expenses = useMemo(() => data?.data || [], [data]);
  const payments = useMemo(() => paymentsData?.data || [], [paymentsData]);

  // Filter expenses by date and category
  const filteredExpenses = useMemo(() => {
    let filtered = expenses;

    // Filter by date
    if (startDate || endDate) {
      filtered = filtered.filter((expense) => {
        const expenseDate = new Date(expense.flow_date || expense.created_at);
        const start = startDate ? new Date(startDate) : new Date("2000-01-01");
        const end = endDate ? new Date(endDate) : new Date("2099-12-31");
        return expenseDate >= start && expenseDate <= end;
      });
    }

    // Filter by category
    if (categoryFilter && categoryFilter !== "all") {
      filtered = filtered.filter((expense) => expense.category === categoryFilter);
    }

    return filtered.sort((a, b) => {
      const dateA = new Date(a.flow_date || a.created_at);
      const dateB = new Date(b.flow_date || b.created_at);
      return dateB - dateA; // Newest first
    });
  }, [expenses, startDate, endDate, categoryFilter]);

  // Filter payments (income) by date
  const filteredPayments = useMemo(() => {
    if (!startDate && !endDate) return payments;
    
    return payments.filter((payment) => {
      const paymentDate = new Date(payment.payment_date || payment.created_at);
      const start = startDate ? new Date(startDate) : new Date("2000-01-01");
      const end = endDate ? new Date(endDate) : new Date("2099-12-31");
      return paymentDate >= start && paymentDate <= end;
    });
  }, [payments, startDate, endDate]);

  // Calculate summary statistics
  const summary = useMemo(() => {
    const totalExpenses = filteredExpenses.reduce((sum, exp) => sum + Number(exp.amount || 0), 0);
    const totalIncome = filteredPayments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
    const netProfit = totalIncome - totalExpenses;
    
    const byCategory = {};
    filteredExpenses.forEach((exp) => {
      const cat = exp.category || "other";
      byCategory[cat] = (byCategory[cat] || 0) + Number(exp.amount || 0);
    });

    const topCategory = Object.entries(byCategory)
      .sort((a, b) => b[1] - a[1])[0];

    // Monthly breakdown with profit
    const byMonth = {};
    
    // Add expenses by month
    filteredExpenses.forEach((exp) => {
      const date = new Date(exp.flow_date || exp.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      if (!byMonth[monthKey]) {
        byMonth[monthKey] = { expenses: 0, income: 0, month: monthKey };
      }
      byMonth[monthKey].expenses += Number(exp.amount || 0);
    });

    // Add income by month
    filteredPayments.forEach((payment) => {
      const date = new Date(payment.payment_date || payment.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      if (!byMonth[monthKey]) {
        byMonth[monthKey] = { expenses: 0, income: 0, month: monthKey };
      }
      byMonth[monthKey].income += Number(payment.amount || 0);
    });

    // Calculate profit for each month
    Object.keys(byMonth).forEach((monthKey) => {
      byMonth[monthKey].profit = byMonth[monthKey].income - byMonth[monthKey].expenses;
    });

    return {
      totalExpenses,
      totalIncome,
      netProfit,
      count: filteredExpenses.length,
      average: filteredExpenses.length ? totalExpenses / filteredExpenses.length : 0,
      byCategory,
      topCategory: topCategory ? { name: topCategory[0], amount: topCategory[1] } : null,
      byMonth,
    };
  }, [filteredExpenses, filteredPayments]);

  // Export to Excel (CSV format)
  const exportToExcel = () => {
    const dateRange = startDate && endDate ? `${startDate}_to_${endDate}` : "all_time";
    const filename = `melona_expenses_${dateRange}.csv`;

    let csv = "Melona Printing - Expense History Report\n";
    csv += `Generated: ${new Date().toLocaleString()}\n`;
    csv += `Date Range: ${startDate || "All"} to ${endDate || "All"}\n`;
    csv += `Category Filter: ${categoryFilter === "all" ? "All Categories" : CATEGORIES.find(c => c.value === categoryFilter)?.label}\n\n`;

    // Summary
    csv += "SUMMARY\n";
    csv += "Metric,Value\n";
    csv += `Total Income,${summary.totalIncome.toFixed(2)} ETB\n`;
    csv += `Total Expenses,${summary.totalExpenses.toFixed(2)} ETB\n`;
    csv += `Net Profit,${summary.netProfit.toFixed(2)} ETB\n`;
    csv += `Number of Transactions,${summary.count}\n`;
    csv += `Average Expense,${summary.average.toFixed(2)} ETB\n`;
    if (summary.topCategory) {
      csv += `Top Expense Category,${CATEGORIES.find(c => c.value === summary.topCategory.name)?.label} (${summary.topCategory.amount.toFixed(2)} ETB)\n`;
    }
    csv += "\n";

    // Category Breakdown
    csv += "EXPENSES BY CATEGORY\n";
    csv += "Category,Amount (ETB),Percentage\n";
    Object.entries(summary.byCategory)
      .sort((a, b) => b[1] - a[1])
      .forEach(([cat, amount]) => {
        const percentage = summary.total > 0 ? ((amount / summary.total) * 100).toFixed(1) : 0;
        const catLabel = CATEGORIES.find(c => c.value === cat)?.label || cat;
        csv += `${catLabel},${amount.toFixed(2)},${percentage}%\n`;
      });
    csv += "\n";

    // Monthly Breakdown
    csv += "MONTHLY BREAKDOWN\n";
    csv += "Month,Income (ETB),Expenses (ETB),Profit (ETB)\n";
    Object.values(summary.byMonth)
      .sort((a, b) => a.month.localeCompare(b.month))
      .forEach((month) => {
        csv += `${month.month},${month.income.toFixed(2)},${month.expenses.toFixed(2)},${month.profit.toFixed(2)}\n`;
      });
    csv += "\n";

    // Detailed Transactions
    csv += "DETAILED EXPENSE TRANSACTIONS\n";
    csv += "Date,Category,Description,Amount (ETB),Payment Method,Reference Number,Notes\n";
    filteredExpenses.forEach((expense) => {
      const date = shortDate(expense.flow_date || expense.created_at);
      const category = CATEGORIES.find(c => c.value === expense.category)?.label || expense.category;
      const description = (expense.description || "").replace(/,/g, ";"); // Replace commas for CSV
      const notes = (expense.notes || "").replace(/,/g, ";");
      csv += `${date},${category},${description},${Number(expense.amount).toFixed(2)},${expense.payment_method || "N/A"},${expense.reference_number || "N/A"},${notes}\n`;
    });

    // Create and download
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isLoading || paymentsLoading) {
    return <div className="loading-state">Loading expense history...</div>;
  }

  return (
    <div className="dashboard-page">
      <SectionHeader
        eyebrow="Expense History"
        title="Complete expense transaction history with filtering and export"
        description="View, filter, and export all business expenses by date and category"
        action={
          <div style={{ display: "flex", gap: "12px" }}>
            <button
              className="btn-secondary"
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <FiFilter /> {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
            <button
              className="btn-primary"
              type="button"
              onClick={exportToExcel}
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
              disabled={filteredExpenses.length === 0}
            >
              <FiDownload /> Export to Excel
            </button>
          </div>
        }
      />

      {/* Filters Section */}
      {showFilters && (
        <div className="panel" style={{ marginBottom: "1.5rem", padding: "20px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <FiCalendar />
                <label style={{ fontWeight: 600, fontSize: "0.875rem" }}>Start Date:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{
                    padding: "8px 12px",
                    border: "2px solid var(--border, #e5e7eb)",
                    borderRadius: "6px",
                    fontSize: "0.875rem",
                  }}
                />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <FiCalendar />
                <label style={{ fontWeight: 600, fontSize: "0.875rem" }}>End Date:</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{
                    padding: "8px 12px",
                    border: "2px solid var(--border, #e5e7eb)",
                    borderRadius: "6px",
                    fontSize: "0.875rem",
                  }}
                />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <FiTag />
                <label style={{ fontWeight: 600, fontSize: "0.875rem" }}>Category:</label>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  style={{
                    padding: "8px 12px",
                    border: "2px solid var(--border, #e5e7eb)",
                    borderRadius: "6px",
                    fontSize: "0.875rem",
                    minWidth: "200px",
                  }}
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>
              {(startDate || endDate || categoryFilter !== "all") && (
                <button
                  className="btn-secondary"
                  type="button"
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                    setCategoryFilter("all");
                  }}
                  style={{ fontSize: "0.875rem" }}
                >
                  Clear All Filters
                </button>
              )}
            </div>
            {(startDate || endDate || categoryFilter !== "all") && (
              <div style={{ 
                color: "var(--brand, #e8702a)", 
                fontSize: "0.875rem", 
                fontWeight: 600,
                padding: "12px",
                background: "var(--brand-light, #fdf0e8)",
                borderRadius: "6px",
                border: "1px solid var(--brand, #e8702a)"
              }}>
                Active Filters: 
                {startDate && ` From ${startDate}`}
                {endDate && ` To ${endDate}`}
                {categoryFilter !== "all" && ` | Category: ${CATEGORIES.find(c => c.value === categoryFilter)?.label}`}
                {` | Showing ${filteredExpenses.length} of ${expenses.length} expenses`}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Summary Statistics */}
      <div className="stats-grid">
        <StatCard
          label="Total Expenses"
          value={money(summary.totalExpenses)}
          tone="warning"
          helper={`${summary.count} transactions`}
        />
        <StatCard
          label="Net Profit"
          value={money(summary.netProfit)}
          tone={summary.netProfit >= 0 ? "success" : "warning"}
          helper={summary.netProfit >= 0 ? "Profitable period" : "Loss period"}
        />
        <StatCard
          label="Top Expense"
          value={summary.topCategory ? CATEGORIES.find(c => c.value === summary.topCategory.name)?.label : "N/A"}
          tone="default"
          helper={summary.topCategory ? money(summary.topCategory.amount) : ""}
        />
        <StatCard
          label="Transactions"
          value={summary.count}
          tone="default"
          helper={`Out of ${expenses.length} total`}
        />
      </div>

      {/* Income vs Expenses Card */}
      <div className="panel" style={{ marginBottom: "1.5rem", padding: "24px" }}>
        <h3 style={{ marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
          <FiTrendingUp style={{ color: "var(--brand, #e8702a)" }} /> Financial Overview
        </h3>
        <div style={{ 
          display: "grid", 
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
          gap: "20px" 
        }}>
          <div style={{ 
            padding: "20px", 
            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            borderRadius: "12px",
            color: "white"
          }}>
            <div style={{ fontSize: "0.875rem", opacity: 0.9, marginBottom: "8px" }}>Total Income</div>
            <div style={{ fontSize: "1.75rem", fontWeight: 700 }}>{formatCurrency(summary.totalIncome)} ETB</div>
            <div style={{ fontSize: "0.75rem", opacity: 0.8, marginTop: "4px" }}>
              From {filteredPayments.length} payments
            </div>
          </div>
          
          <div style={{ 
            padding: "20px", 
            background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
            borderRadius: "12px",
            color: "white"
          }}>
            <div style={{ fontSize: "0.875rem", opacity: 0.9, marginBottom: "8px" }}>Total Expenses</div>
            <div style={{ fontSize: "1.75rem", fontWeight: 700 }}>{formatCurrency(summary.totalExpenses)} ETB</div>
            <div style={{ fontSize: "0.75rem", opacity: 0.8, marginTop: "4px" }}>
              From {summary.count} transactions
            </div>
          </div>
          
          <div style={{ 
            padding: "20px", 
            background: summary.netProfit >= 0 
              ? "linear-gradient(135deg, #e8702a 0%, #8f3412 100%)"
              : "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            borderRadius: "12px",
            color: "white"
          }}>
            <div style={{ fontSize: "0.875rem", opacity: 0.9, marginBottom: "8px" }}>Net Profit</div>
            <div style={{ fontSize: "1.75rem", fontWeight: 700 }}>
              {summary.netProfit >= 0 ? "+" : ""}{formatCurrency(summary.netProfit)} ETB
            </div>
            <div style={{ fontSize: "0.75rem", opacity: 0.8, marginTop: "4px" }}>
              {summary.netProfit >= 0 ? "✓ Profitable" : "⚠ Loss"}
            </div>
          </div>
        </div>
      </div>

      {/* Category Breakdown */}
      {Object.keys(summary.byCategory).length > 0 && (
        <div className="panel" style={{ marginBottom: "1.5rem" }}>
          <h3 style={{ marginBottom: "1rem", display: "flex", alignItems: "center", gap: "8px" }}>
            <FiTrendingDown /> Expenses by Category
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "12px" }}>
            {Object.entries(summary.byCategory)
              .sort((a, b) => b[1] - a[1])
              .map(([cat, amount]) => {
                const percentage = summary.totalExpenses > 0 ? ((amount / summary.totalExpenses) * 100).toFixed(1) : 0;
                const catLabel = CATEGORIES.find(c => c.value === cat)?.label || cat;
                return (
                  <div
                    key={cat}
                    style={{
                      padding: "16px",
                      background: "var(--bg-subtle, #f9fafb)",
                      borderRadius: "8px",
                      border: "1px solid var(--border, #e5e7eb)",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                      <CategoryDot category={cat} />
                      <span style={{ fontSize: "0.875rem", fontWeight: 600 }}>{catLabel}</span>
                    </div>
                    <div style={{ fontSize: "1.25rem", fontWeight: 700, color: "var(--brand, #e8702a)" }}>
                      {formatCurrency(amount)} ETB
                    </div>
                    <div style={{ fontSize: "0.75rem", color: "var(--text-muted, #888)", marginTop: "4px" }}>
                      {percentage}% of total
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Expense History Table */}
      <div className="panel" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "20px 20px 16px", borderBottom: "1px solid var(--border, #e5e7eb)" }}>
          <h3 style={{ margin: 0 }}>Expense Transactions</h3>
        </div>
        <div className="table-wrap">
          {filteredExpenses.length === 0 ? (
            <div style={{ padding: "40px", textAlign: "center", color: "var(--text-muted, #888)" }}>
              <FiDollarSign size={48} style={{ opacity: 0.3, marginBottom: "16px" }} />
              <p style={{ fontSize: "1rem", fontWeight: 600, marginBottom: "8px" }}>No expenses found</p>
              <p style={{ fontSize: "0.875rem" }}>
                {startDate || endDate || categoryFilter !== "all"
                  ? "Try adjusting your filters"
                  : "No expense records available"}
              </p>
            </div>
          ) : (
            <table className="data-table" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Category</th>
                  <th>Description</th>
                  <th style={{ textAlign: "right" }}>Amount</th>
                  <th>Payment Method</th>
                  <th>Reference</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {filteredExpenses.map((expense) => (
                  <tr key={expense.id}>
                    <td style={{ whiteSpace: "nowrap", fontSize: "0.875rem" }}>
                      {shortDate(expense.flow_date || expense.created_at)}
                    </td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <CategoryDot category={expense.category} />
                        <span style={{ fontSize: "0.875rem" }}>
                          {CATEGORIES.find(c => c.value === expense.category)?.label || expense.category}
                        </span>
                      </div>
                    </td>
                    <td style={{ maxWidth: "300px" }}>
                      <strong style={{ display: "block", fontSize: "0.875rem" }}>{expense.description}</strong>
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 600, fontSize: "0.9375rem", color: "var(--danger, #b93826)" }}>
                      {formatCurrency(expense.amount)} ETB
                    </td>
                    <td style={{ fontSize: "0.875rem", textTransform: "capitalize" }}>
                      {(expense.payment_method || "N/A").replace("_", " ")}
                    </td>
                    <td style={{ fontSize: "0.875rem", color: "var(--text-muted, #888)" }}>
                      {expense.reference_number || "—"}
                    </td>
                    <td style={{ fontSize: "0.8125rem", color: "var(--text-muted, #888)", maxWidth: "200px" }}>
                      {expense.notes || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: "2px solid var(--border, #e5e7eb)", background: "var(--bg-subtle, #f9fafb)" }}>
                  <td colSpan="3" style={{ fontWeight: 700, fontSize: "0.9375rem" }}>
                    TOTAL ({filteredExpenses.length} transactions)
                  </td>
                  <td style={{ textAlign: "right", fontWeight: 700, fontSize: "1.125rem", color: "var(--danger, #b93826)" }}>
                    {formatCurrency(summary.totalExpenses)} ETB
                  </td>
                  <td colSpan="3"></td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpenseHistoryPage;
